import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, tap } from 'rxjs/operators';

@Injectable()
export class ThrottleRequestsInterceptor implements HttpInterceptor {
    private requestsWithTimestamps = new Map<
        string,
        {
            timestamp: number;
            response: HttpResponse<any> | null;
            error: HttpErrorResponse | null;
            inProgress: Observable<HttpEvent<any>> | null;
        }
    >();
    private readonly TIME_WINDOW = 4000;

    /**
     * Bumped by every write. A replayed GET body is only correct as long as nothing was written in
     * between, so a write empties the cache — and a GET still in flight when the write happened may
     * carry the pre-write state, so its response is not cached either (it reaches its own
     * subscribers, nobody else).
     *
     * Measured on e2e `datev-conflict-stored-purpose.spec.ts` C2(i1): the upload page loads the empty
     * report, "Speichern und weiter" PUTs it with its accounts, and the mapping page reloads the same
     * `annualreport/encryptedId/<id>` 2.8 s after the first load — answered from the cache with the
     * pre-upload body, so the mapping showed no account at all.
     *
     * This covers a route only where a write sent through this client is what makes it stale. A
     * route that goes stale WITHOUT such a write — a poll, a socket event (`…/ai/sessions/<id>` after
     * `ai:job:completed`), a change made by the server or another tab — still needs its entry in
     * NEVER_THROTTLE below.
     */
    private writeGeneration = 0;

    /**
     * Endpoints that must never be answered from the 4-second cache, because a replayed response is
     * by definition a stale one.
     *
     * `…/ai/sessions/<id>` is POLLED twice within a few hundred milliseconds — once right after
     * `POST /ai/jobs` returns 202, and once when the `ai:job:completed` socket event arrives. With
     * the cache the second poll gets the FIRST poll's body back, i.e. the session as it looked
     * before the agent finished (`RUNNING`, `output: null`). Any agent that finishes inside the
     * window then leaves the UI stuck on "running" forever — no further request is ever made.
     *
     * `…/projects/<id>/documents[/<id>]` (ABCP closing documents, REV-21376) is not polled but has
     * the same property: it changes as a SIDE EFFECT of other actions, so a replayed body is stale.
     * Its three filter-pill counts are served by the server precisely because the client cannot
     * compute them (ADR-0009 §9a), so a cached response also freezes the numbers the user is watching
     * for confirmation. Concretely: closing the SharePoint settings modal reloads the list, because
     * the user may have bound a folder while it was open — and that close usually lands INSIDE the
     * window after the page's own first load, so the reload was answered from the cache and the new
     * documents never appeared, with no request on the wire to explain why. The reload after a
     * settled upload has the identical shape.
     *
     * Safe to exempt: the list effect uses `switchMap`, so a fast second pill click CANCELS the
     * in-flight request rather than piling another one on top of it.
     *
     * `…/projects/<id>/accounts/reconciliation-status` (ABCP accounts overview poll, REV-21869 #162)
     * is the same pattern again: `startReconciliationPoll$` self-schedules via `timer()`, and an
     * in-app return (tab switch, no `page.goto`) to the accounts overview inside the 4 s window
     * dispatched `loadReconciliationStatus` but the request never reached the wire — cached from the
     * PREVIOUS chain's last poll, so no `…Success`/`…Failure` action ever followed. Measured with a
     * throwaway spec + a full `/api/` network log (see impl.md "Runde 4"): with a return < 4 s after
     * the last request, the route is silent for 15 s+; with a >4.5 s wait (outside TIME_WINDOW) it
     * fires within ~100 ms every time. Control experiment isolates the interceptor as the sole cause.
     * `…/accounts/classification-status` has the identical bug. It was out of #162's scope and stood
     * here as "tracked, not fixed" until the Sprint-3 code review picked it up as S3-13 — the fix was
     * one line, sitting next to its own repair on two neighbouring routes. Listed below since
     * 09.09.2026; the route is `project-accounts.service.ts:94`, the exact sibling of
     * `reconciliation-status`.
     *
     * `…/ai/org-settings/<organizationId>` (AI settings page, REV-21869 N-2.3 Welle 2, case 3b) is
     * the same pattern a third time: `persistService()`'s success branch deliberately RE-FETCHES
     * rather than pulling the new state forward by object spread, because only the server's own
     * `serviceConsent()` computation can tell ON from PARTIAL (`ai-settings.page.ts:353-356`). A
     * customer enables a paid service bundle, the `PATCH …/services` returns 204, and the page
     * immediately calls `loadSettings()` — but if that GET lands inside the 4 s window opened by the
     * page's OWN initial load, the interceptor hands back the cached PRE-WRITE body instead of
     * putting the request on the wire. The tile then shows the bundle as still OFF, with 0 running
     * members, right after the customer was told the write succeeded — a paid switch that visibly
     * did not take, with no failed request anywhere to explain why. Measured: the gesture completing
     * 1542 ms after the page's load GET reproduces the stale render every time (0 GETs reach the wire
     * after the write); the same gesture 4.5 s later is served fresh and renders correctly.
     *
     * `…/projects/<id>/accounts/<accountId>/adjustments` and `…/projects/<id>/adjustments` (ABCP
     * correction bookings, REV-22008) — the same pattern a fourth time. The account view loads its
     * corrections on entry; a booking created from its `Korrekturbuchungen` pane a few seconds later
     * triggers `reloadAccountAfterWrite$`, and that GET came back from the cache with the PRE-WRITE
     * list — the header counted the new booking while the pane it was created in did not show it.
     * Measured on the Acrevis seed: the reload fires, 0 requests reach the wire, the pane keeps its old
     * rows. The closing-wide register reloads after every write the same way.
     *
     * `…/api/persons` and `…/person-projects/<id>` (REV-22155) — the same pattern a fifth time.
     * Deleting a person returns to the overview, whose list reload lands inside the window of its own
     * first load: the cached list still holds the deleted person ("Aktiv 7", row still there until F5).
     * Likewise "Synchronisieren" reloads the person project, and the cached body carries the old
     * `lastTimeSynchronizedMM`, so the sync hint came back.
     */
    private static readonly NEVER_THROTTLE: readonly RegExp[] = [
        /\/ai\/sessions\//,
        /\/projects\/[^/]+\/documents(\/|$)/,
        /\/projects\/[^/]+\/accounts\/reconciliation-status(\?|$)/,
        /\/projects\/[^/]+\/accounts\/classification-status(\?|$)/,
        /\/ai\/org-settings\/[^/]+(?:\?|$)/,
        /\/projects\/[^/]+\/accounts\/[^/]+\/adjustments(\?|$)/,
        /\/projects\/[^/]+\/adjustments(\/|\?|$)/,
        /\/api\/persons(\/|\?|$)/,
        /\/person-projects\/[^/]+(\?|$)/,
    ];

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // Skip throttling for PUT, POST and PATCH requests
        if (req.method === 'PUT' || req.method === 'POST' || req.method === 'PATCH') {
            this.forgetCachedResponses();
            return next.handle(req);
        }

        if (ThrottleRequestsInterceptor.NEVER_THROTTLE.some((pattern) => pattern.test(req.url))) {
            if (req.method === 'DELETE') {
                this.forgetCachedResponses();
            }
            return next.handle(req);
        }

        const requestKey = this.getRequestKey(req);
        const now = Date.now();

        // Check if the request is cached or in progress. A DELETE goes through here too, so a second
        // identical DELETE inside the window (double click) joins the first instead of being sent again.
        const entry = this.requestsWithTimestamps.get(requestKey);
        const lastRequestTime = entry?.timestamp;

        if (entry && lastRequestTime && now - lastRequestTime < this.TIME_WINDOW) {
            if (entry.inProgress) {
                // If request is in progress, return the observable for in-progress request
                return entry.inProgress;
            }

            if (entry.response) {
                // Return cached response if available
                return of(entry.response);
            } else if (entry.error) {
                // Return cached error if available
                console.log('DEBUG1:', 'Returning cached error');
                return throwError(() => entry.error);
            }
        }

        // A DELETE that reaches the wire is a write. Emptied before its own entry is set below, so the
        // entry survives and deduplicates the next identical DELETE.
        if (req.method === 'DELETE') {
            this.forgetCachedResponses();
        }
        const generation = this.writeGeneration;

        const shared = next.handle(req).pipe(
            tap((event) => {
                if (event instanceof HttpResponse && generation === this.writeGeneration) {
                    // Cache the successful response
                    this.requestsWithTimestamps.set(requestKey, {
                        timestamp: now,
                        response: event,
                        error: null,
                        inProgress: null,
                    });
                }
            }),
            catchError((error: HttpErrorResponse) => {
                console.log('DEBUG1:', 'HttpErrorResponse encountered');
                // Cache the error response immediately to prevent duplicate requests
                if (generation === this.writeGeneration) {
                    this.requestsWithTimestamps.set(requestKey, {
                        timestamp: now,
                        response: null,
                        error: error,
                        inProgress: null,
                    });
                }

                return throwError(() => error); // Rethrow the error for downstream handling
            }),

            finalize(() => {
                setTimeout(() => {
                    this.requestsWithTimestamps.delete(requestKey);
                }, this.TIME_WINDOW);
            }),
            shareReplay(),
        );

        this.requestsWithTimestamps.set(requestKey, {
            timestamp: now,
            response: null,
            error: null,
            inProgress: shared,
        });

        return shared;
    }

    private forgetCachedResponses(): void {
        this.writeGeneration++;
        this.requestsWithTimestamps.clear();
    }

    private fnv1aHash(str: string): number {
        let hash = 2166136261;
        for (let i = 0; i < str.length; i++) {
            hash ^= str.charCodeAt(i);
            hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
        }
        return hash >>> 0; // Convert to unsigned 32-bit integer
    }

    private getRequestKey(req: HttpRequest<any>): string {
        if (req.method === 'GET') {
            return `${req.method} ${req.urlWithParams}`;
        } else {
            const bodyString = req.body ? JSON.stringify(req.body) : '';
            const bodyHash = bodyString ? this.fnv1aHash(bodyString).toString() : '';
            return `${req.method} ${req.urlWithParams} ${bodyHash}`;
        }
    }
}
